import { useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuthContext, PMContext, WS_URL } from "../App";
import {
  playLoginWelcomeSound,
  playDingDingSound,
  playFriendOnlineSound,
  playFriendOfflineSound
} from "../utils/sounds";

/**
 * Global PM / room-invite / friend / system-alert notification subscriber.
 * Mounts ONCE at the App root (inside BrowserRouter so we can use useNavigate),
 * keeps a single /ws/pm-notifications WebSocket open across every route, and
 * sends a 20-second heartbeat to defeat ingress idle-timeout disconnects.
 *
 * Stability: the WebSocket lifecycle effect depends only on token + user_id.
 * Other context callbacks (openPMWindow / navigate / logout) are mirrored into
 * refs so changing context references never retriggers a reconnect.
 */
export default function GlobalPMNotifications() {
  const { user, token, logout } = useContext(AuthContext);
  const { openPMWindow } = useContext(PMContext);
  const navigate = useNavigate();

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);

  // Mirror callbacks into refs so the connect effect deps stay stable
  const openPMWindowRef = useRef(openPMWindow);
  const navigateRef = useRef(navigate);
  const logoutRef = useRef(logout);
  useEffect(() => { openPMWindowRef.current = openPMWindow; }, [openPMWindow]);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  useEffect(() => { logoutRef.current = logout; }, [logout]);

  const userId = user?.user_id;

  useEffect(() => {
    if (!token || !userId) return undefined;

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) return;

      const ws = new WebSocket(`${WS_URL}/ws/pm-notifications?token=${token}`);

      ws.onopen = () => {
        console.log("[GlobalPM] connected");
        const alreadyPlayed = sessionStorage.getItem("loginSoundPlayed");
        const isLoggingOut = sessionStorage.getItem("isLoggingOut");
        if (!alreadyPlayed && !isLoggingOut) {
          playLoginWelcomeSound();
          sessionStorage.setItem("loginSoundPlayed", "true");
        }
        sessionStorage.removeItem("isLoggingOut");

        if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: "ping" })); } catch (_) { /* noop */ }
          }
        }, 20000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "new_pm") {
            const senderId = data.sender_id || data.message?.sender_id;
            const senderName = data.sender_username || data.message?.sender_username || "New message";
            toast.info(`New message from ${senderName}`, { duration: 3000 });
            playDingDingSound();
            if (senderId) openPMWindowRef.current?.(senderId, senderName);
          } else if (data.type === "room_invite") {
            toast(
              <div className="flex flex-col gap-2">
                <p className="font-bold">{data.from_username} invites you to {data.room_name}!</p>
                <div className="flex gap-2">
                  <button
                    className="px-3 py-1 bg-green-600 text-white rounded text-sm font-bold"
                    onClick={() => { navigateRef.current?.(`/room/${data.room_id}`); toast.dismiss(); }}
                    data-testid="accept-room-invite-btn"
                  >Join</button>
                  <button
                    className="px-3 py-1 bg-gray-600 text-white rounded text-sm"
                    onClick={() => toast.dismiss()}
                    data-testid="ignore-room-invite-btn"
                  >Ignore</button>
                </div>
              </div>,
              { duration: 15000 }
            );
            playDingDingSound();
          } else if (data.type === "force_join") {
            toast.success(`Access granted! Joining ${data.room_name || "room"}...`);
            navigateRef.current?.(`/room/${data.room_id}`);
          } else if (data.type === "friend_online") {
            toast.success(`🟢 ${data.username} is now online!`, { duration: 4000, icon: "👋" });
            playFriendOnlineSound();
          } else if (data.type === "friend_offline") {
            toast.info(`🔴 ${data.username} went offline`, { duration: 3000, icon: "👋" });
            playFriendOfflineSound();
          } else if (data.type === "emergency_alert") {
            toast.error(data.message, { duration: 10000, icon: "🚨" });
          } else if (data.type === "system_alert") {
            toast.info(data.content, { duration: 10000, icon: "🤖" });
          } else if (data.type === "voice_banned") {
            // Admin has banned this user's IP from voice chat — live notification
            const expiry = data.expires_at
              ? `Expires: ${new Date(data.expires_at).toLocaleString()}`
              : "Permanent";
            toast.error(
              `🎙️ You have been banned from using voice chat.\n` +
              (data.reason ? `Reason: ${data.reason}\n` : "") +
              expiry,
              { duration: 10000 }
            );
            // Force-disable any active voice session by emitting a window event the
            // ChatRoomPage listens to (so we don't need a hard coupling to its state).
            window.dispatchEvent(new CustomEvent("voice:banned", { detail: data }));
          }
        } catch (error) {
          console.error("[GlobalPM] parse error:", error);
        }
      };

      ws.onclose = (event) => {
        console.log("[GlobalPM] closed", event.code);
        if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);

        if (event.code === 4006 || event.reason === "SESSION_EXPIRED") {
          toast.error("You have been logged out because you logged in from another location.", { duration: 5000 });
          logoutRef.current?.(false);
          return;
        }

        if (sessionStorage.getItem("isLoggingOut")) return;
        if (cancelled) return;

        reconnectTimeoutRef.current = setTimeout(connect, 2000);
      };

      ws.onerror = (e) => console.error("[GlobalPM] error:", e);

      wsRef.current = ws;
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) { /* noop */ }
      }
      wsRef.current = null;
    };
  }, [token, userId]);

  return null;
}
